(() => {
  const STORAGE_KEY = 'onlyfindz_wishlist';
  const STYLE_ID = 'wishlist-styles';
  const WISHLIST_CONSENT_CATEGORY = 'preferences';

  const parseJson = (value, fallback) => {
    try {
      return JSON.parse(value);
    } catch (error) {
      console.error(error);
      return fallback;
    }
  };

  const wishlistStorageAllowed = () => {
    const consentApi = window.OnlyFindzConsent;
    if (!consentApi || typeof consentApi.allows !== 'function') return false;
    return consentApi.allows(WISHLIST_CONSENT_CATEGORY) === true;
  };

  const getItems = () => {
    if (!wishlistStorageAllowed()) return [];
    const items = parseJson(window.localStorage.getItem(STORAGE_KEY) || '[]', []);
    return Array.isArray(items) ? items : [];
  };

  const setItems = (items) => {
    if (!wishlistStorageAllowed()) return false;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items } }));
    return true;
  };

  const findIndex = (handle) => getItems().findIndex((item) => item.handle === handle);

  const strings = () =>
    window.onlyFindzWishlistStrings || {
      add: 'Add to wishlist',
      remove: 'Remove from wishlist',
      heading: 'Wishlist',
      empty: 'Your wishlist is empty.',
      count: '[count]',
      continueShopping: 'Continue shopping',
    };

  const injectStyles = () => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .wishlist-button {
        position: absolute;
        inset-block-start: 0.9rem;
        inset-inline-end: 0.9rem;
        z-index: 4;
      }
      .wishlist-button__trigger {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 3.2rem;
        height: 3.2rem;
        min-width: 0;
        min-height: 0;
        padding: 0;
        border-radius: 50%;
        border: 1px solid rgba(17, 24, 39, 0.08);
        background: rgba(255, 255, 255, 0.92);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        color: rgba(var(--color-foreground), 0.75);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.10);
        cursor: pointer;
        transition: transform 180ms ease, background 180ms ease, color 180ms ease, box-shadow 180ms ease;
      }
      .wishlist-button__trigger:hover,
      .wishlist-button__trigger:focus-visible {
        transform: scale(1.06);
        box-shadow: 0 4px 14px rgba(0, 0, 0, 0.16);
      }
      .wishlist-button__trigger.is-active {
        color: var(--marketplace-accent, #0bd1d9);
        background: rgba(255, 255, 255, 0.96);
      }
      .wishlist-button__trigger.is-pulsing {
        animation: wishlistPulse 260ms ease;
      }
      .wishlist-button__icon svg {
        display: block;
        width: 1.4rem;
        height: 1.4rem;
        fill: none;
        stroke: currentColor;
        stroke-width: 2;
        flex-shrink: 0;
      }
      .wishlist-button__trigger.is-active .wishlist-button__icon svg {
        fill: currentColor;
      }
      @keyframes wishlistPulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.16); }
        100% { transform: scale(1); }
      }
      .wishlist-page {
        margin-top: 2.4rem;
      }
      .wishlist-page__header {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        gap: 1.2rem;
        margin-bottom: 2rem;
      }
      .wishlist-page__grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 1.2rem;
      }
      .wishlist-page__empty {
        padding: 2.4rem;
        border: 1px solid var(--marketplace-border, rgba(17, 24, 39, 0.08));
        border-radius: 0.8rem;
        background: var(--marketplace-panel, rgb(var(--color-background)));
      }
      .wishlist-page__empty p { margin-bottom: 1.6rem; }
      .wishlist-page__count {
        color: var(--marketplace-muted, rgba(15, 23, 32, 0.68));
      }
      [data-wishlist-count][data-count]:not([data-count="0"])::after {
        content: attr(data-count);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 1.8rem;
        height: 1.8rem;
        margin-inline-start: 0.6rem;
        padding: 0 0.5rem;
        border-radius: 999px;
        background: var(--marketplace-accent, #0bd1d9);
        color: var(--marketplace-accent-contrast, #041719);
        font-size: 1.1rem;
        font-weight: 700;
        line-height: 1;
      }
      @media screen and (min-width: 600px) {
        .wishlist-page__grid {
          grid-template-columns: repeat(3, minmax(0, 1fr));
        }
      }
      @media screen and (min-width: 750px) {
        .wishlist-page__grid {
          grid-template-columns: repeat(4, minmax(0, 1fr));
        }
      }
      @media screen and (min-width: 990px) {
        .wishlist-page__grid {
          grid-template-columns: repeat(5, minmax(0, 1fr));
        }
      }
    `;
    document.head.appendChild(style);
  };

  const replaceCount = (count) => strings().count.replace('[count]', count);

  const updateBadge = () => {
    const count = getItems().length;
    document.querySelectorAll('[data-wishlist-count]').forEach((element) => {
      element.dataset.count = String(count);
      element.textContent = replaceCount(count);
    });
  };

  const CART_ICON_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" fill-rule="evenodd" class="icon icon-cart"><path d="M10 1.5a4 4 0 0 0-4 4H4a1 1 0 0 0-1 1l.75 10.5A1.5 1.5 0 0 0 5.25 18.5h9.5a1.5 1.5 0 0 0 1.5-1.5L17 6.5a1 1 0 0 0-1-1h-2a4 4 0 0 0-4-4zm3 4a3 3 0 0 0-6 0h6z"/></svg>';

  const addToCart = async (handle, button) => {
    try {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      const res = await fetch('/products/' + handle + '.js');
      const product = await res.json();
      const variantId = product.variants?.[0]?.id;
      if (!variantId) throw new Error('No variant');
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });
      window.dispatchEvent(new CustomEvent('cart:updated'));
      // Reload cart count badge
      fetch('/cart.js').then(r => r.json()).then(cart => {
        document.querySelectorAll('.cart-count-bubble').forEach(el => {
          const span = el.querySelector('span[aria-hidden]') || el;
          span.textContent = cart.item_count;
        });
      });
    } catch (e) {
      console.error(e);
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };

  // Mirrors snippets/onlyfindz-product-card.liquid so wishlist cards render
  // identically to every other product card on the site.
  const buildCard = (item) => {
    const available = item.available !== 'false';

    const article = document.createElement('article');
    article.className = 'onlyfindz-home__product-card';

    const mediaLink = document.createElement('a');
    mediaLink.className = 'onlyfindz-home__product-media';
    mediaLink.href = item.url;
    if (!available) {
      const badge = document.createElement('span');
      badge.className = 'onlyfindz-home__soldout-badge';
      badge.textContent = 'Ausverkauft';
      mediaLink.appendChild(badge);
    }
    if (item.image) {
      const image = document.createElement('img');
      image.className = 'onlyfindz-home__product-image';
      image.src = item.image;
      image.alt = item.title;
      image.loading = 'lazy';
      image.width = 533;
      image.height = 533;
      mediaLink.appendChild(image);
    } else {
      mediaLink.insertAdjacentHTML('beforeend', `
        <div class="onlyfindz-image-placeholder onlyfindz-image-placeholder--card" role="img" aria-label="Produktfoto wird in Kürze ergänzt">
          <span class="onlyfindz-image-placeholder__icon" aria-hidden="true">
            <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="4.5" y="9.5" width="39" height="29" rx="4" stroke="currentColor" stroke-width="2"/>
              <circle cx="16.5" cy="19.5" r="3.25" stroke="currentColor" stroke-width="2"/>
              <path d="M4.5 32L16 22.5L23.5 28.5L31.5 20.5L43.5 31.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
          <span class="onlyfindz-image-placeholder__copy">
            <span class="onlyfindz-image-placeholder__title">Bild folgt in Kürze</span>
            <span class="onlyfindz-image-placeholder__subtitle">Produktfoto wird ergänzt</span>
          </span>
        </div>
      `);
    }

    const wishlistButton = document.createElement('wishlist-button');
    wishlistButton.className = 'wishlist-button';
    wishlistButton.dataset.productHandle = item.handle;
    wishlistButton.dataset.productTitle = item.title;
    wishlistButton.dataset.productVendor = item.vendor || '';
    wishlistButton.dataset.productPrice = item.price;
    wishlistButton.dataset.productCompareAtPrice = item.compareAtPrice || '';
    wishlistButton.dataset.productAvailable = item.available || 'true';
    wishlistButton.dataset.productImage = item.image || '';
    wishlistButton.dataset.productUrl = item.url;
    wishlistButton.innerHTML = `
      <button type="button" class="wishlist-button__trigger" aria-pressed="false" aria-label="${strings().remove}">
        <span class="visually-hidden wishlist-button__label">${strings().remove}</span>
        <span class="wishlist-button__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" focusable="false"><path d="M12 21.35 10.55 20C5.4 15.24 2 12.09 2 8.25 2 5.1 4.42 2.75 7.5 2.75c1.74 0 3.41.82 4.5 2.1 1.09-1.28 2.76-2.1 4.5-2.1 3.08 0 5.5 2.35 5.5 5.5 0 3.84-3.4 6.99-8.55 11.76L12 21.35Z"></path></svg>
        </span>
      </button>
    `;

    const body = document.createElement('div');
    body.className = 'onlyfindz-home__product-body';

    const vendor = document.createElement('p');
    vendor.className = 'onlyfindz-home__vendor';
    vendor.textContent = item.vendor || '';

    const title = document.createElement('h3');
    title.className = 'onlyfindz-home__product-title';
    const titleLink = document.createElement('a');
    titleLink.className = 'onlyfindz-home__product-link';
    titleLink.href = item.url;
    titleLink.textContent = item.title;
    title.appendChild(titleLink);

    const priceRow = document.createElement('div');
    priceRow.className = 'onlyfindz-home__price-row';
    const price = document.createElement('span');
    price.className = 'onlyfindz-home__price';
    price.textContent = item.price;
    priceRow.appendChild(price);
    if (item.compareAtPrice) {
      const compare = document.createElement('span');
      compare.className = 'onlyfindz-home__compare';
      compare.textContent = item.compareAtPrice;
      priceRow.appendChild(compare);
    }

    const availability = document.createElement('span');
    availability.className = 'onlyfindz-home__availability' + (available ? '' : ' onlyfindz-home__availability--soldout');
    availability.textContent = available ? 'Auf Lager' : 'Nicht verfügbar';

    const actions = document.createElement('div');
    actions.className = 'onlyfindz-home__card-actions';

    const detailsLink = document.createElement('a');
    detailsLink.className = 'onlyfindz-home__details-link';
    detailsLink.href = item.url;
    detailsLink.textContent = 'Details';
    actions.appendChild(detailsLink);

    if (available) {
      const atcButton = document.createElement('button');
      atcButton.type = 'button';
      atcButton.className = 'onlyfindz-home__icon-button';
      atcButton.setAttribute('aria-label', item.title + ' in den Warenkorb');
      atcButton.innerHTML = CART_ICON_SVG;
      atcButton.addEventListener('click', () => addToCart(item.handle, atcButton));
      actions.appendChild(atcButton);
    }

    body.append(vendor, title, priceRow, availability, actions);
    article.append(mediaLink, wishlistButton, body);
    return article;
  };

  const renderWishlistPage = () => {
    const mount = document.getElementById('wishlist-grid');
    if (!mount) return;

    const items = getItems();
    mount.innerHTML = '';
    mount.classList.add('wishlist-page');

    const header = document.createElement('div');
    header.className = 'wishlist-page__header';
    const heading = document.createElement('h1');
    heading.className = 'h2';
    heading.textContent = strings().heading;
    const count = document.createElement('p');
    count.className = 'wishlist-page__count';
    count.textContent = replaceCount(items.length);
    header.append(heading, count);
    mount.appendChild(header);

    if (!items.length) {
      const empty = document.createElement('div');
      empty.className = 'wishlist-page__empty';
      const message = document.createElement('p');
      message.textContent = strings().empty;
      const cta = document.createElement('a');
      cta.className = 'button';
      cta.href = window.routes?.all_products_collection_url || '/collections/all';
      cta.textContent = strings().continueShopping;
      empty.append(message, cta);
      mount.appendChild(empty);
      return;
    }

    const grid = document.createElement('div');
    grid.className = 'wishlist-page__grid';
    items.forEach((item) => grid.appendChild(buildCard(item)));
    mount.appendChild(grid);
  };

  const updateAllButtons = () => {
    document.querySelectorAll('wishlist-button').forEach((button) => button.updateState?.());
  };

  class WishlistButton extends HTMLElement {
    connectedCallback() {
      try {
        injectStyles();
        this.button = this.querySelector('.wishlist-button__trigger');
        this.label = this.querySelector('.wishlist-button__label');
        if (!this.button) return;
        this.onToggle = this.onToggle.bind(this);
        this.onExternalUpdate = this.onExternalUpdate.bind(this);
        this.button.addEventListener('click', this.onToggle);
        window.addEventListener('wishlist:updated', this.onExternalUpdate);
        window.addEventListener('storage', this.onExternalUpdate);
        this.updateState();
      } catch (error) {
        console.error(error);
      }
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onToggle);
      window.removeEventListener('wishlist:updated', this.onExternalUpdate);
      window.removeEventListener('storage', this.onExternalUpdate);
    }

    get item() {
      return {
        handle: this.dataset.productHandle,
        title: this.dataset.productTitle,
        vendor: this.dataset.productVendor,
        price: this.dataset.productPrice,
        compareAtPrice: this.dataset.productCompareAtPrice,
        available: this.dataset.productAvailable,
        image: this.dataset.productImage,
        url: this.dataset.productUrl,
      };
    }

    onToggle() {
      if (!wishlistStorageAllowed()) {
        window.OnlyFindzConsent?.open?.();
        return;
      }

      const items = getItems();
      const index = items.findIndex((item) => item.handle === this.dataset.productHandle);
      if (index === -1) {
        items.push(this.item);
      } else {
        items.splice(index, 1);
      }
      this.button.classList.remove('is-pulsing');
      requestAnimationFrame(() => this.button.classList.add('is-pulsing'));
      setTimeout(() => this.button?.classList.remove('is-pulsing'), 260);
      if (!setItems(items)) return;
      updateBadge();
      renderWishlistPage();
      updateAllButtons();
    }

    onExternalUpdate(event) {
      if (event.type === 'storage' && event.key !== STORAGE_KEY) return;
      this.updateState();
      updateBadge();
      renderWishlistPage();
    }

    updateState() {
      const active = findIndex(this.dataset.productHandle) !== -1;
      this.button?.classList.toggle('is-active', active);
      this.button?.setAttribute('aria-pressed', String(active));
      this.button?.setAttribute('aria-label', active ? strings().remove : strings().add);
      if (this.label) this.label.textContent = active ? strings().remove : strings().add;
    }
  }

  if (!customElements.get('wishlist-button')) {
    customElements.define('wishlist-button', WishlistButton);
  }

  document.addEventListener('DOMContentLoaded', () => {
    injectStyles();
    updateBadge();
    renderWishlistPage();
    window.addEventListener('onlyfindz:consent-updated', () => {
      updateBadge();
      renderWishlistPage();
      updateAllButtons();
    });
    window.addEventListener('storage', (event) => {
      if (event.key !== STORAGE_KEY) return;
      updateBadge();
      renderWishlistPage();
      updateAllButtons();
    });
    window.addEventListener('wishlist:updated', () => {
      updateBadge();
      renderWishlistPage();
    });
  });
})();
